/**
 * PokemonFullSheet — 5-tab dossier modal (Dossier PR 2 + PR 3).
 *
 * Replaces the legacy single-pane Full Sheet with a tabbed dossier per
 * the design doc + EN-4/EN-7. Detail view shared by three callers:
 *
 *   - PokedexScene rows (cta='back-only')
 *   - PartySheetScene orb-tap (cta='set-as-lead' off-lead; 'back-only' on lead)
 *
 * (DraftScene is OUT of scope per SR-1 — keeps its own flip-card.)
 *
 * Architecture: DOM overlay parented to `#game`, NOT a Phaser scene. The
 * scene argument is kept on the constructor signature so callers can
 * register SHUTDOWN cleanup. Pure presentation — no `runState` subscription
 * per SR-3 (live mode captures data at modal-open, never reactively
 * re-renders). Death JOIN happens at construction time via careerStore
 * lookup; missing alive member → caller chose snapshot mode upstream.
 *
 * Tab order + default tab driven by `callbacks.context` per DR-2 / EN-7:
 *   - 'live-context'     → STATS / ABOUT / MOVES / LEARNSET / MATCHUPS;
 *                          default STATS.
 *   - 'snapshot-context' → ABOUT / STATS / MOVES / LEARNSET / MATCHUPS;
 *                          default ABOUT.
 *
 * PR 2 ships ABOUT + STATS content; MOVES / LEARNSET / MATCHUPS bodies
 * render skeletons until PR 3 fills them (atomic ship event per EN-8).
 *
 * Reduced motion + skeleton timing handled by CSS — see tokens.css
 * `@media (prefers-reduced-motion: reduce)` block under the DOSSIER
 * section. The component itself is animation-agnostic.
 */
import Phaser from 'phaser';
import type { Generation } from '@pkmn/data';
import { careerStore } from '../state/careerStore';
import { loadGen3 } from '../data/dex';
import { getFlavor } from '../data/flavorText';
import { computeStat, type IvKey } from '../data/computeStat';
import { NATURE_MODS, type Nature } from '../data/natures';
import {
  composeDossierProse,
  type DossierProseInput,
  type FullSheetData,
} from '../data/dossierProse';
import {
  computeDefensiveChips,
  computeOffensiveChips,
  type EfficacyDex,
} from '../data/typeEfficacy';
import {
  createDossierIdentityHeader,
  type DeathSummary,
} from '../ui/dossierIdentityHeader';
import { DossierTabStrip } from '../ui/dossierTabStrip';
import {
  getDefaultFullSheetTab,
  getFullSheetTabOrder,
  type DossierContext,
  type FullSheetTab,
} from '../ui/dossierTabStripLogic';
import { createEfficacyChip } from '../ui/efficacyChip';
import {
  createEggMoveSectionLabel,
  createLearnsetRow,
  type LearnsetRowVariant,
} from '../ui/learnsetRow';
import { createDefensiveMatchupChart } from '../ui/defensiveMatchupChart';
import {
  isLegendSeen,
  markLegendSeen,
  shouldShowLegend,
} from '../ui/dossierLegendLogic';
import { mapDisplayName } from './titleSlotData';

/** Primary CTA in the sticky footer.
 *
 *   'back-only'   — PokedexScene rows, lead-orb tap (DR-4A).
 *   'set-as-lead' — PartySheetScene orb-tap on slots 1+.
 *
 * The legacy 'take' value was removed in PR 4a STRUCTURAL (SR-1 —
 * DraftScene never opens FullSheet; the value was dead code). */
export type FullSheetCta = 'back-only' | 'set-as-lead';

export interface FullSheetCallbacks {
  /** Caller-determined context — drives tab order + default tab. */
  context: DossierContext;
  /** Sticky-footer CTA. Defaults to 'back-only'. */
  cta?: FullSheetCta;
  onBack: () => void;
  onTake?: () => void;
}

const TAB_LABELS: Record<FullSheetTab, string> = {
  about: 'About',
  stats: 'Stats',
  moves: 'Moves',
  learnset: 'Learnset',
  matchups: 'Matchups',
};

/** Cartridge-feel abbreviations for ≤360 px viewports (SR-11). Manual
 *  per-tab table — slice(0,3) of "About" is "ABO", not the cartridge
 *  "ABT". */
const TAB_ABBREV: Record<FullSheetTab, string> = {
  about: 'ABT',
  stats: 'STA',
  moves: 'MOV',
  learnset: 'LRN',
  matchups: 'MTC',
};

const STAT_ROWS: ReadonlyArray<{ key: IvKey; label: string }> = [
  { key: 'hp',  label: 'HP'  },
  { key: 'atk', label: 'Atk' },
  { key: 'def', label: 'Def' },
  { key: 'spa', label: 'SpA' },
  { key: 'spd', label: 'SpD' },
  { key: 'spe', label: 'Spe' },
];

function titleCase(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/** "BOLD +DEF / -ATK" — uppercase cartridge nature display. */
function formatNature(n: Nature): string {
  const mod = NATURE_MODS[n];
  const upper = titleCase(n).toUpperCase();
  if (mod.plus && mod.minus) {
    return `${upper} +${mod.plus.toUpperCase()} / -${mod.minus.toUpperCase()}`;
  }
  return `${upper} (NEUTRAL)`;
}

/** "Overgrow · boosts grass moves at low HP" — ability one-liner. */
function formatAbility(name: string, desc: string): string {
  if (!name) return 'Ability —';
  if (!desc) return name;
  return `${name} · ${desc[0]!.toLowerCase()}${desc.slice(1)}`;
}

export class PokemonFullSheet {
  private root: HTMLDivElement | null = null;
  private card: HTMLDivElement | null = null;
  private tabBodies: Map<FullSheetTab, HTMLElement> = new Map();
  private tabStrip: DossierTabStrip<FullSheetTab> | null = null;
  private visibleTabs: FullSheetTab[] = [];

  constructor(
    scene: Phaser.Scene,
    private data: FullSheetData,
    private callbacks: FullSheetCallbacks,
  ) {
    this.render();
    // Defensive teardown if the host scene shuts down without an explicit
    // destroy() call (e.g. forced scene swap mid-modal).
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    void this.populateAsync();
  }

  /** Root DOM element accessor — exposed for PR 4b.2's PartySheetScene
   *  fade fix (EN-5b). Returns null after destroy(). */
  getRootElement(): HTMLElement | null {
    return this.root;
  }

  destroy(): void {
    this.tabStrip?.destroy();
    this.tabStrip = null;
    this.root?.remove();
    this.root = null;
    this.card = null;
    this.tabBodies.clear();
  }

  private render(): void {
    const gameEl = document.getElementById('game');
    if (!gameEl) return;

    const root = document.createElement('div');
    root.className = 'dossier-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', `${this.data.offer.displayName} dossier`);
    // Backdrop click closes. Card click stops propagation below.
    root.addEventListener('click', (e) => {
      if (e.target === root) this.callbacks.onBack();
    });

    const card = document.createElement('div');
    card.className = 'dossier-card';
    card.addEventListener('click', (e) => e.stopPropagation());
    const primaryType = (this.data.offer.types[0] ?? 'normal').toLowerCase();
    card.style.setProperty('--card-type-tint', `var(--type-${primaryType})`);

    const header = createDossierIdentityHeader(this.buildHeaderOptions());
    card.appendChild(header);

    this.visibleTabs = getFullSheetTabOrder(this.callbacks.context);
    const initialTab = getDefaultFullSheetTab(
      this.callbacks.context,
      this.visibleTabs,
    );
    this.tabStrip = new DossierTabStrip<FullSheetTab>({
      visibleTabs: this.visibleTabs,
      initialTab,
      labelFor: (t) => TAB_LABELS[t],
      labelAbbrevFor: (t) => TAB_ABBREV[t],
      onTabChange: (t) => this.showTab(t),
    });
    card.appendChild(this.tabStrip.getElement());

    const bodies = document.createElement('div');
    bodies.className = 'dossier-body';
    for (const tab of this.visibleTabs) {
      const body = document.createElement('div');
      body.className = 'dossier-tab-body';
      body.setAttribute('role', 'tabpanel');
      body.dataset.tab = tab;
      if (tab !== initialTab) body.hidden = true;
      body.appendChild(this.buildSkeletonFor(tab));
      this.tabBodies.set(tab, body);
      bodies.appendChild(body);
    }
    card.appendChild(bodies);

    card.appendChild(this.buildFooter());
    root.appendChild(card);
    gameEl.appendChild(root);

    this.root = root;
    this.card = card;
  }

  /** Derive identity-header options from the data discriminator. */
  private buildHeaderOptions() {
    const data = this.data;
    const offer = data.offer;
    let level = offer.level;
    let hp: { current: number; max: number } | null = null;
    let death: DeathSummary | null = null;
    let notPicked = false;
    if (data.kind === 'live') {
      level = data.member.level;
      // Live JOIN: if the member's id matches a careerStore death entry,
      // the player is viewing a mid-run-dead member from THIS RUN >
      // DRAFTED (per EN-7 row 3). Show death caption + desaturated sprite.
      const career = careerStore.findById(data.member.id);
      if (
        career?.diedAt != null
        && career.diedTo
        && career.diedZone
        && career.levelAtDeath != null
      ) {
        death = {
          diedTo: career.diedTo,
          diedZone: career.diedZone,
          levelAtDeath: career.levelAtDeath,
        };
      } else {
        hp = { current: data.member.hp.current, max: data.member.hp.max };
      }
    } else if (data.kind === 'career-snapshot') {
      level = data.entry.levelAtDeath ?? data.entry.level;
      if (
        data.entry.diedTo
        && data.entry.diedZone
        && data.entry.levelAtDeath != null
      ) {
        death = {
          diedTo: data.entry.diedTo,
          diedZone: data.entry.diedZone,
          levelAtDeath: data.entry.levelAtDeath,
        };
      }
    } else {
      notPicked = true;
    }
    return {
      name: offer.displayName,
      gender: offer.gender,
      level,
      types: offer.types,
      natureLine: formatNature(offer.nature),
      abilityLine: formatAbility(offer.abilityName, offer.abilityDesc),
      spriteUrl: offer.dexId
        ? `/assets/pokemon/orb/${offer.dexId}.gif`
        : undefined,
      hp,
      death,
      notPicked,
    };
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'dossier-footer';
    const cta = this.callbacks.cta ?? 'back-only';
    footer.dataset.cta = cta;

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'dossier-footer__back';
    back.textContent = '← Back';
    back.addEventListener('click', () => this.callbacks.onBack());
    footer.appendChild(back);

    if (cta === 'set-as-lead') {
      const primary = document.createElement('button');
      primary.type = 'button';
      primary.className = 'dossier-footer__cta';
      primary.textContent = 'Set as lead';
      primary.addEventListener('click', () => this.callbacks.onTake?.());
      footer.appendChild(primary);
    }
    return footer;
  }

  /** Generic skeleton — 3 stacked ghost lines per DR-3. Used at mount; the
   *  tab-specific populateAsync (STATS sync, ABOUT async, MOVES/LEARNSET/
   *  MATCHUPS in PR 3) replaces the skeleton once data resolves. */
  private buildSkeletonFor(_tab: FullSheetTab): HTMLElement {
    const skel = document.createElement('div');
    skel.className = 'dossier-tab-body__skeleton';
    for (let i = 0; i < 3; i++) {
      const ghost = document.createElement('div');
      ghost.className = 'dossier-skeleton';
      ghost.style.height = '16px';
      ghost.style.marginBottom = '8px';
      ghost.style.width = `${100 - i * 12}%`;
      skel.appendChild(ghost);
    }
    return skel;
  }

  private showTab(tab: FullSheetTab): void {
    for (const [t, body] of this.tabBodies) {
      body.hidden = t !== tab;
    }
    // SR-10: first tab-switch AWAY from MOVES clears the session legend
    // (alongside the tap-to-dismiss path on the legend banner itself).
    if (tab !== 'moves') {
      try {
        markLegendSeen(window.sessionStorage);
      } catch {
        // sessionStorage unavailable (private browsing) — legend will
        // simply re-appear next open; acceptable degradation.
      }
      const moves = this.tabBodies.get('moves');
      moves?.querySelector('.dossier-moves-legend')?.remove();
    }
  }

  private async populateAsync(): Promise<void> {
    // STATS is sync — render immediately.
    this.renderStatsTab();

    // The remaining four tabs share `loadGen3()`. ABOUT consumes species
    // data; MOVES needs move metadata + the type chart; LEARNSET needs
    // species learnsets; MATCHUPS needs the type chart. One await covers
    // all; per-tab try/catch around the consumers degrades gracefully
    // (SR-6 — no catch-all, named contexts only).
    let gen: Generation | null = null;
    try {
      gen = await loadGen3();
    } catch (err) {
      console.warn('[dossier] @pkmn/dex resolve failed:', err);
    }
    // Defensive bail — sheet may have been destroyed mid-await.
    if (!this.root) return;

    const species = gen?.species.get(this.data.offer.species) ?? null;
    this.renderAboutTab(species ? { weightkg: species.weightkg } : null);

    if (gen) {
      this.renderMovesTab(gen);
      void this.renderLearnsetTab(gen);
      this.renderMatchupsTab(gen);
    } else {
      // SR-6 degradation: empty states across the gen-dependent tabs.
      this.renderMovesDegraded();
      this.renderLearnsetDegraded();
      this.renderMatchupsDegraded();
    }
  }

  private renderAboutTab(species: { weightkg?: number } | null): void {
    const body = this.tabBodies.get('about');
    if (!body) return;
    const offer = this.data.offer;
    const flavor = getFlavor(offer.species);
    const mapName = offer.caughtAt?.mapName ?? '';
    const caughtMapDisplay = mapName ? mapDisplayName(mapName) : null;
    const input: DossierProseInput = {
      data: this.data,
      species,
      flavor,
      caughtMapDisplay,
    };
    const prose = composeDossierProse(input);
    body.replaceChildren();
    const intro = document.createElement('p');
    intro.className = 'dossier-prose-paragraph';
    intro.textContent = prose.intro;
    body.appendChild(intro);
    if (prose.flavorPullQuote) {
      const quote = document.createElement('p');
      quote.className = 'dossier-prose-pullquote';
      quote.textContent = prose.flavorPullQuote;
      body.appendChild(quote);
    }
    if (prose.facts) {
      const facts = document.createElement('p');
      facts.className = 'dossier-prose-paragraph';
      facts.textContent = prose.facts;
      body.appendChild(facts);
    }
    if (prose.epitaph) {
      const ep = document.createElement('p');
      ep.className = 'dossier-prose-paragraph dossier-prose-paragraph--epitaph';
      ep.textContent = prose.epitaph;
      body.appendChild(ep);
    }
  }

  private renderStatsTab(): void {
    const body = this.tabBodies.get('stats');
    if (!body) return;
    body.replaceChildren();

    const offer = this.data.offer;
    const level = this.derivedLevel();
    const mod = NATURE_MODS[offer.nature];
    const primaryType = (offer.types[0] ?? 'normal').toLowerCase();

    const table = document.createElement('div');
    table.className = 'dossier-stats-table';

    let ivTotal = 0;
    for (const { key, label } of STAT_ROWS) {
      const iv = offer.ivs[key];
      const base = offer.baseStats[key];
      const final = base > 0
        ? computeStat(key, base, iv, level, offer.nature)
        : 0;
      ivTotal += iv;

      const row = document.createElement('div');
      row.className = 'dossier-stat-row';
      let arrow = '';
      if (mod.plus === key) {
        row.dataset.nature = 'plus';
        arrow = ' ↑';
      } else if (mod.minus === key) {
        row.dataset.nature = 'minus';
        arrow = ' ↓';
      } else {
        row.dataset.nature = 'neutral';
      }

      const lblEl = document.createElement('span');
      lblEl.className = 'dossier-stat-row__label';
      lblEl.textContent = `${label}${arrow}`;
      row.appendChild(lblEl);

      const bar = document.createElement('div');
      bar.className = 'dossier-iv-bar';
      bar.style.setProperty('--iv-fill', `var(--type-${primaryType})`);
      const filled = Math.round((iv / 31) * 16);
      for (let i = 0; i < 16; i++) {
        const cell = document.createElement('span');
        cell.className = 'dossier-iv-bar__cell';
        if (i < filled) cell.dataset.filled = 'true';
        bar.appendChild(cell);
      }
      row.appendChild(bar);

      const ivNum = document.createElement('span');
      ivNum.className = 'dossier-stat-row__iv';
      ivNum.textContent = String(iv);
      row.appendChild(ivNum);

      const baseNum = document.createElement('span');
      baseNum.className = 'dossier-stat-row__base';
      baseNum.textContent = String(base);
      row.appendChild(baseNum);

      const finalNum = document.createElement('span');
      finalNum.className = 'dossier-stat-row__final';
      finalNum.textContent = String(final);
      row.appendChild(finalNum);

      table.appendChild(row);
    }

    body.appendChild(table);

    const ivTotalLine = document.createElement('div');
    ivTotalLine.className = 'dossier-stats-iv-total';
    ivTotalLine.textContent = `IV TOTAL: ${ivTotal} / 186`;
    body.appendChild(ivTotalLine);
  }

  /** Level X used for the STATS Final column.
   *    live            → member.level
   *    career-snapshot → levelAtDeath (fallback offer.level)
   *    snapshot        → offer.level
   */
  private derivedLevel(): number {
    const data = this.data;
    if (data.kind === 'live') return data.member.level;
    if (data.kind === 'career-snapshot') {
      return data.entry.levelAtDeath ?? data.entry.level;
    }
    return data.offer.level;
  }

  /** Current moveset for LEARNSET's "replaced" diff.
   *    live → member.moves (the live current set)
   *    else → offer.moves (frozen at draft / death snapshot)
   */
  private derivedMoves(): string[] {
    const data = this.data;
    if (data.kind === 'live') return data.member.moves ?? [];
    return data.offer.moves;
  }

  // ─── MOVES tab (DR-7 + SR-10 + SR-6) ──────────────────────────────

  private renderMovesTab(gen: Generation): void {
    const body = this.tabBodies.get('moves');
    if (!body) return;
    body.replaceChildren();

    // SR-10 / DR-10 — show the legend on first MOVES open per session.
    // Tap dismisses; first tab-switch away also clears via showTab().
    let storage: Storage | null = null;
    try {
      storage = window.sessionStorage;
    } catch {
      // Sandboxed (in-app webview) — legend will show every session;
      // acceptable degradation.
    }
    if (storage && shouldShowLegend(isLegendSeen(storage))) {
      body.appendChild(this.buildMovesLegend(storage));
    }

    const list = document.createElement('ul');
    list.className = 'dossier-moves-list';
    const offer = this.data.offer;
    for (let i = 0; i < offer.moves.length; i++) {
      const moveId = offer.moves[i];
      if (!moveId) continue;
      const moveDisplay = offer.movesDisplay[i] ?? titleCase(moveId);
      const isEgg = !!offer.eggPullMove && moveId === offer.eggPullMove;
      const move = gen.moves.get(moveId);
      const moveType = move?.type ?? '';
      const category = (move?.category ?? 'Status') as 'Physical' | 'Special' | 'Status';
      list.appendChild(
        this.buildMoveRow({
          name: moveDisplay,
          type: moveType,
          category,
          basePower: move?.basePower ?? 0,
          accuracy: move?.accuracy ?? true,
          pp: move?.pp ?? 0,
          isEgg,
          gen,
        }),
      );
    }
    body.appendChild(list);
  }

  private renderMovesDegraded(): void {
    const body = this.tabBodies.get('moves');
    if (!body) return;
    body.replaceChildren();
    const list = document.createElement('ul');
    list.className = 'dossier-moves-list';
    const offer = this.data.offer;
    for (let i = 0; i < offer.moves.length; i++) {
      const moveId = offer.moves[i];
      if (!moveId) continue;
      const row = document.createElement('li');
      row.className = 'dossier-move-row dossier-move-row--degraded';
      const name = document.createElement('span');
      name.className = 'dossier-move-row__name';
      name.textContent = offer.movesDisplay[i] ?? titleCase(moveId);
      row.appendChild(name);
      list.appendChild(row);
    }
    body.appendChild(list);
  }

  private buildMovesLegend(storage: Storage): HTMLElement {
    const legend = document.createElement('div');
    legend.className = 'dossier-moves-legend';
    legend.textContent =
      '● phy · ○ spc · chips show super-effective + ineffective only';
    legend.addEventListener('click', () => {
      markLegendSeen(storage);
      legend.remove();
    });
    return legend;
  }

  private buildMoveRow(opts: {
    name: string;
    type: string;
    category: 'Physical' | 'Special' | 'Status';
    basePower: number;
    accuracy: number | true;
    pp: number;
    isEgg: boolean;
    gen: Generation;
  }): HTMLLIElement {
    const row = document.createElement('li');
    row.className = 'dossier-move-row';
    if (opts.isEgg) row.dataset.egg = 'true';

    const catGlyph =
      opts.category === 'Physical' ? '●'
      : opts.category === 'Special' ? '○'
      : '◆';
    const dot = document.createElement('span');
    dot.className = `dossier-move-row__cat dossier-move-row__cat--${opts.category.toLowerCase()}`;
    dot.textContent = catGlyph;
    dot.setAttribute('aria-label', opts.category);
    row.appendChild(dot);

    if (opts.type) {
      const typeChip = document.createElement('span');
      typeChip.className = 'dossier-move-row__type';
      typeChip.style.setProperty('--row-type', `var(--type-${opts.type.toLowerCase()})`);
      typeChip.textContent = opts.type;
      row.appendChild(typeChip);
    }

    const name = document.createElement('span');
    name.className = 'dossier-move-row__name';
    name.textContent = opts.name;
    row.appendChild(name);

    const stats = document.createElement('span');
    stats.className = 'dossier-move-row__stats';
    const bp = opts.basePower > 0 ? `${opts.basePower}BP` : '—';
    const acc = opts.accuracy === true ? '—' : `${opts.accuracy}%`;
    const pp = opts.pp > 0 ? `${opts.pp}PP` : '—';
    stats.textContent = `${bp} · ${acc} · ${pp}`;
    row.appendChild(stats);

    // Inline efficacy chips — attacking moves only. Status moves get no
    // chip strip (no type damage to show).
    if (opts.category !== 'Status' && opts.type) {
      const chips = document.createElement('span');
      chips.className = 'dossier-move-row__chips';
      const chipList = computeOffensiveChips(opts.type, this.efficacyDex(opts.gen));
      for (const chip of chipList) {
        chips.appendChild(
          createEfficacyChip({ type: chip.type, multiplier: chip.multiplier }),
        );
      }
      row.appendChild(chips);
    }

    return row;
  }

  /** Adapter: the @pkmn/data Type instance has `damageTaken` at runtime
   *  (via `Object.assign(this, type)` in its constructor) but the public
   *  TS surface hides it — only `totalEffectiveness` is typed. The cast
   *  through `unknown` widens the type without claiming a specific public
   *  contract; the runtime shape matches `EfficacyDex` exactly. */
  private efficacyDex(gen: Generation): EfficacyDex {
    return { types: gen.types as unknown as EfficacyDex['types'] };
  }

  // ─── LEARNSET tab (DR-4 + DR-9 + SR-6) ─────────────────────────────

  private async renderLearnsetTab(gen: Generation): Promise<void> {
    const body = this.tabBodies.get('learnset');
    if (!body) return;
    let learnsetData;
    try {
      learnsetData = await gen.learnsets.get(this.data.offer.species);
    } catch (err) {
      console.warn('[dossier:learnset] resolve failed:', err);
      body.replaceChildren(this.buildEmpty('Learnset data unavailable.'));
      return;
    }
    if (!this.root) return;
    const learnsetMap = learnsetData?.learnset;
    if (!learnsetMap) {
      body.replaceChildren(this.buildEmpty('Learnset data unavailable.'));
      return;
    }

    body.replaceChildren();
    const offer = this.data.offer;
    const currentLevel = this.derivedLevel();
    const currentMoves = new Set(this.derivedMoves());

    // Parse Gen-3 level-up sources from the learnset registry. Source
    // strings: '3L1', '3L7' = "Gen 3, Level N". Other sources (TMs, HMs,
    // tutors, breeding) are ignored — v0.1 dossier shows level-up only.
    const curve: Array<{ moveId: string; level: number }> = [];
    for (const [moveId, sources] of Object.entries(learnsetMap)) {
      let earliest: number | null = null;
      for (const src of sources as string[]) {
        if (src.length < 3 || src[0] !== '3' || src[1] !== 'L') continue;
        const lvl = Number.parseInt(src.slice(2), 10);
        if (!Number.isFinite(lvl)) continue;
        if (earliest === null || lvl < earliest) earliest = lvl;
      }
      if (earliest !== null) curve.push({ moveId, level: earliest });
    }
    curve.sort((a, b) => a.level - b.level || a.moveId.localeCompare(b.moveId));

    // Skipped offers were never drafted → only render "Would learn next".
    // (Live + career-snapshot show both sections.)
    const isSkipped = this.data.kind === 'snapshot';
    const learnedSoFar = isSkipped
      ? []
      : curve.filter((c) => c.level <= currentLevel);
    const willLearn = curve.filter((c) => c.level > currentLevel);

    // EGG MOVE section first (DR-4 — replaces the ✨ emoji marker).
    if (offer.eggPullMove) {
      body.appendChild(createEggMoveSectionLabel());
      const eggMove = gen.moves.get(offer.eggPullMove);
      const eggIdx = offer.moves.indexOf(offer.eggPullMove);
      const eggMoveName =
        eggIdx >= 0
          ? (offer.movesDisplay[eggIdx] ?? eggMove?.name ?? titleCase(offer.eggPullMove))
          : (eggMove?.name ?? titleCase(offer.eggPullMove));
      const eggList = document.createElement('ul');
      eggList.className = 'dossier-learnset-list';
      eggList.appendChild(
        createLearnsetRow({
          level: 0,
          moveName: eggMoveName,
          variant: 'already-learned',
          type: eggMove?.type ?? '',
        }),
      );
      body.appendChild(eggList);
    }

    if (learnedSoFar.length > 0) {
      body.appendChild(this.buildSectionLabel('ALREADY LEARNED'));
      const list = document.createElement('ul');
      list.className = 'dossier-learnset-list';
      for (const entry of learnedSoFar) {
        const moveData = gen.moves.get(entry.moveId);
        const moveName = moveData?.name ?? titleCase(entry.moveId);
        const moveType = moveData?.type ?? '';
        // DR-9: if the species curve says this should be known but it's
        // not in the current moveset, render strike-through "replaced".
        const isCurrent = currentMoves.has(entry.moveId);
        const variant: LearnsetRowVariant = isCurrent ? 'already-learned' : 'replaced';
        list.appendChild(
          createLearnsetRow({
            level: entry.level,
            moveName,
            variant,
            type: moveType,
          }),
        );
      }
      body.appendChild(list);
    }

    if (willLearn.length > 0) {
      body.appendChild(
        this.buildSectionLabel(isSkipped ? 'WOULD LEARN NEXT' : 'WILL LEARN'),
      );
      const list = document.createElement('ul');
      list.className = 'dossier-learnset-list';
      for (const entry of willLearn) {
        const moveData = gen.moves.get(entry.moveId);
        const moveName = moveData?.name ?? titleCase(entry.moveId);
        const moveType = moveData?.type ?? '';
        list.appendChild(
          createLearnsetRow({
            level: entry.level,
            moveName,
            variant: 'will-learn',
            type: moveType,
          }),
        );
      }
      body.appendChild(list);
    }

    if (learnedSoFar.length === 0 && willLearn.length === 0 && !offer.eggPullMove) {
      body.appendChild(this.buildEmpty('No level-up moves known.'));
    }
  }

  private renderLearnsetDegraded(): void {
    const body = this.tabBodies.get('learnset');
    if (!body) return;
    body.replaceChildren(this.buildEmpty('Learnset data unavailable.'));
  }

  // ─── MATCHUPS tab (DR-8 + SR-6) ────────────────────────────────────

  private renderMatchupsTab(gen: Generation): void {
    const body = this.tabBodies.get('matchups');
    if (!body) return;
    body.replaceChildren();
    const offerTypes = this.data.offer.types;
    if (offerTypes.length === 0) {
      body.appendChild(this.buildEmpty('Type data unavailable.'));
      return;
    }
    const typeTuple: readonly [string] | readonly [string, string] =
      offerTypes.length >= 2
        ? [offerTypes[0]!, offerTypes[1]!]
        : [offerTypes[0]!];
    const buckets = computeDefensiveChips(typeTuple, this.efficacyDex(gen));
    body.appendChild(createDefensiveMatchupChart({ buckets }));
  }

  private renderMatchupsDegraded(): void {
    const body = this.tabBodies.get('matchups');
    if (!body) return;
    body.replaceChildren(this.buildEmpty('Type data unavailable.'));
  }

  // ─── Small helpers ─────────────────────────────────────────────────

  private buildSectionLabel(text: string): HTMLElement {
    const lbl = document.createElement('div');
    lbl.className = 'dossier-learnset-section-label';
    lbl.textContent = text;
    return lbl;
  }

  private buildEmpty(message: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'dossier-empty';
    el.textContent = message;
    return el;
  }
}
